import type { SettlementDetail, SettlementPeriod } from '@facture/contracts'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { HandCoinsIcon, HardHatIcon, PlusIcon, ReceiptTextIcon, WalletIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { Stagger } from '@/components/motion'
import { Button } from '@/components/ui/button'
import { columnHelper, DataTable, useDebounced } from '@/components/ui/data-table'
import {
  ClearFilters,
  FilterBar,
  FilterDateRange,
  FilterSearch,
  FilterSelect,
  FilterTabs,
} from '@/components/ui/filter-bar'
import { EmptyState, Page, PageHeader } from '@/components/ui/page'
import { fmtCol, PayrollDisclaimer, rangeLabel } from '@/features/payroll/payroll-ui'
import { projectOptionsQuery } from '@/features/projects/api'
import { settlementsQuery } from '@/features/settlements/api'
import { SettlementSheet } from '@/features/settlements/settlement-sheet'
import { SettlementStatusBadge, StatTile } from '@/features/settlements/settlement-ui'
import { SettlementWizard, type WizardValue } from '@/features/settlements/settlement-wizard'
import { formatDate } from '@/lib/format'
import { useCan } from '@/lib/session'
import { cn } from '@/lib/utils'

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()

const searchSchema = z.object({
  q: z.string().optional(),
  status: z.enum(['closed', 'draft', 'reverted']).optional(),
  project: z.string().optional(),
  from: isoDate,
  to: isoDate,
  page: z.number().int().min(1).optional(),
  /** Periodo abierto en la ficha lateral. */
  open: z.string().optional(),
})

export const Route = createFileRoute('/_app/projects/settlements')({
  staticData: { crumb: 'Liquidaciones' },
  validateSearch: searchSchema,
  component: SettlementsPage,
})

const col = columnHelper<SettlementPeriod>()

/** Vista global de liquidaciones: los periodos de todas las obras, con filtros estándar. */
function SettlementsPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const canSettle = useCan('payroll:settle')
  const [text, setText] = useState(search.q ?? '')
  const q = useDebounced(text)
  const [wizard, setWizard] = useState<WizardValue | null>(null)
  const setSearch = (patch: Partial<z.infer<typeof searchSchema>>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  // biome-ignore lint/correctness/useExhaustiveDependencies: solo reacciona al texto debounced
  useEffect(() => {
    if (q !== (search.q ?? '')) setSearch({ q: q || undefined, page: undefined })
  }, [q])

  const list = useQuery(
    settlementsQuery({
      search: search.q,
      status: search.status,
      projectId: search.project,
      from: search.from,
      to: search.to,
      page: search.page ?? 1,
      pageSize: 25,
    }),
  )
  const projects = useQuery(projectOptionsQuery)
  const kpis = list.data?.kpis

  const columns = useMemo(
    () => [
      col.accessor('from', {
        header: 'Periodo',
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium tabular">{rangeLabel(row.original.from, row.original.to)}</span>
              <SettlementStatusBadge status={row.original.status} className="md:hidden" />
            </div>
            <div className="truncate text-muted-foreground text-xs md:hidden">
              {row.original.projectCode ? `${row.original.projectCode} · ` : ''}
              {row.original.projectName}
            </div>
            {row.original.notes && (
              <div className="hidden truncate text-muted-foreground text-xs md:block">
                {row.original.notes}
              </div>
            )}
          </div>
        ),
      }),
      col.accessor('projectName', {
        header: 'Proyecto',
        meta: { hideOnMobile: true },
        cell: ({ row }) => (
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-warning-soft text-warning-soft-foreground">
              <HardHatIcon className="size-3.5" />
            </span>
            <div className="min-w-0">
              <div className="truncate text-sm">{row.original.projectName}</div>
              {row.original.projectCode && (
                <div className="truncate font-mono text-[11px] text-muted-foreground">
                  {row.original.projectCode}
                </div>
              )}
            </div>
          </div>
        ),
      }),
      col.accessor('status', {
        header: 'Estado',
        meta: { hideOnMobile: true },
        cell: ({ row }) => <SettlementStatusBadge status={row.original.status} />,
      }),
      col.accessor((p) => p.totals.workerCount, {
        id: 'workers',
        header: 'Trabajadores',
        meta: { align: 'right', hideOnMobile: true },
        cell: ({ getValue }) => <span className="tabular">{getValue()}</span>,
      }),
      col.accessor((p) => p.totals.earned, {
        id: 'earned',
        header: 'Devengado',
        meta: { align: 'right', hideOnMobile: true },
        cell: ({ getValue }) => <span className="tabular">{fmtCol(getValue())}</span>,
      }),
      col.accessor((p) => p.totals.amount, {
        id: 'amount',
        header: 'Liquidado',
        meta: { align: 'right' },
        cell: ({ row }) => (
          <span
            className={cn(
              'font-semibold tabular',
              row.original.status === 'reverted' && 'text-muted-foreground line-through decoration-1',
              row.original.status === 'draft' && 'text-muted-foreground',
            )}
          >
            {fmtCol(row.original.totals.amount)}
          </span>
        ),
      }),
      col.accessor('closedAt', {
        header: 'Cierre',
        meta: { hideOnMobile: true },
        cell: ({ row }) => {
          const p = row.original
          const when =
            p.status === 'reverted' ? p.revertedAt : p.status === 'closed' ? p.closedAt : p.createdAt
          const who =
            p.status === 'reverted'
              ? p.revertedByName
              : p.status === 'closed'
                ? p.closedByName
                : p.createdByName
          return (
            <div className="text-xs">
              <div className="tabular">{when ? formatDate(when, 'd MMM yyyy') : '—'}</div>
              {who && <div className="truncate text-muted-foreground">{who}</div>}
            </div>
          )
        },
      }),
    ],
    [],
  )

  const filtered = !!(search.q || search.status || search.project || search.from || search.to)
  const onDone = (detail: SettlementDetail) => {
    setWizard(null)
    setSearch({ open: detail.id })
  }

  return (
    <Page>
      <PageHeader
        title="Liquidaciones"
        description="Los periodos pagados de todas las obras: qué se liquidó, cuándo y quién lo cerró."
        actions={
          canSettle && (
            <Button onClick={() => setWizard({ projectId: search.project })}>
              <PlusIcon /> Nueva liquidación
            </Button>
          )
        }
      />
      <Stagger className="mb-4 grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        <StatTile
          label="Liquidado"
          icon={HandCoinsIcon}
          accent
          value={kpis?.amount}
          hint={
            kpis
              ? `${kpis.closed} periodo${kpis.closed === 1 ? '' : 's'} cerrado${kpis.closed === 1 ? '' : 's'}`
              : undefined
          }
        />
        <StatTile
          label="Devengado"
          icon={ReceiptTextIcon}
          value={kpis?.earned}
          hint="De esos periodos, sin acumular"
        />
        <StatTile
          label="Saldo por pagar hoy"
          icon={WalletIcon}
          value={kpis?.pendingBalance}
          hint={search.project ? 'En el proyecto filtrado' : 'En todas las obras'}
        />
        <StatTile
          label="Borradores"
          icon={ReceiptTextIcon}
          count
          value={kpis?.drafts}
          tone={kpis?.drafts ? 'warning' : 'muted'}
          hint={
            kpis?.reverted
              ? `${kpis.reverted} revertida${kpis.reverted === 1 ? '' : 's'} en el historial`
              : 'Pendientes de cerrar'
          }
        />
      </Stagger>
      <PayrollDisclaimer className="mb-4" />
      <DataTable
        columns={columns}
        data={list.data?.items}
        loading={list.isFetching}
        getRowId={(p) => p.id}
        onRowClick={(p) => setSearch({ open: p.id })}
        isRowMuted={(p) => p.status === 'reverted'}
        toolbar={
          <FilterBar>
            <FilterTabs
              label="Estado"
              value={search.status ?? 'all'}
              onValueChange={(v) =>
                setSearch({ status: v === 'all' ? undefined : (v as 'closed'), page: undefined })
              }
              options={[
                { value: 'all', label: 'Todas' },
                { value: 'closed', label: 'Cerradas' },
                { value: 'draft', label: 'Borradores' },
                { value: 'reverted', label: 'Revertidas' },
              ]}
            />
            <FilterSearch value={text} onChange={setText} placeholder="Buscar por proyecto o nota…" />
            <FilterSelect
              label="Proyecto"
              width="xl"
              value={search.project ?? 'all'}
              onValueChange={(v) => setSearch({ project: v === 'all' ? undefined : v, page: undefined })}
              options={[
                { value: 'all', label: 'Todos los proyectos' },
                ...(projects.data ?? []).map((p) => ({
                  value: p.id,
                  label: p.code ? `${p.code} · ${p.name}` : p.name,
                })),
              ]}
            />
            <FilterDateRange
              value={{ from: search.from, to: search.to }}
              onChange={(r) => setSearch({ from: r.from, to: r.to, page: undefined })}
            />
            <ClearFilters
              active={filtered}
              onClear={() => {
                setText('')
                setSearch({
                  q: undefined,
                  status: undefined,
                  project: undefined,
                  from: undefined,
                  to: undefined,
                  page: undefined,
                })
              }}
            />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={HandCoinsIcon}
            title={filtered ? 'Sin liquidaciones con esos filtros' : 'Todavía no hay liquidaciones'}
            description={
              filtered
                ? 'Probá con otro estado, proyecto o rango de fechas.'
                : 'Una liquidación paga el saldo de un periodo de una obra y congela esos días.'
            }
            action={
              canSettle &&
              !filtered && (
                <Button onClick={() => setWizard({})}>
                  <PlusIcon /> Nueva liquidación
                </Button>
              )
            }
          />
        }
        pagination={
          list.data && {
            page: list.data.page,
            pageSize: list.data.pageSize,
            total: list.data.total,
            onPageChange: (p) => setSearch({ page: p === 1 ? undefined : p }),
          }
        }
      />
      <SettlementSheet
        id={search.open ?? null}
        showProject
        onOpenChange={(open) => !open && setSearch({ open: undefined })}
        onEdit={(detail) => {
          setSearch({ open: undefined })
          setWizard({ projectId: detail.projectId, draft: detail })
        }}
      />
      <SettlementWizard value={wizard} onClose={() => setWizard(null)} onDone={onDone} />
    </Page>
  )
}
